import React, { useCallback, useEffect, useMemo, useState } from "react";

import Col from "antd/es/col";
import Row from "antd/es/row";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { EntityType, MapFilterEntityType } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import { Button, SelectField } from "@mapmycustomers/ui";

import {
  isAddressAreaSearchQuery,
  isCoordinatesAreaSearchQuery,
  isEntityAreaSearchQuery,
} from "@app/component/input/AreaSearchInput/utils/assert";
import layout from "@app/styles/layout";
import { AreaSearchQuery } from "@app/types/filters/AreaSearchQuery";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import styles from "./CustomizeMapSortModal.module.scss";
import GeoPointSortType from "./enum/GeoPointSortType";
import SortField, { getSortFieldDisplayName } from "./enum/SortField";
import getSortDirectionNameForField from "./getSortDirectionNameForField";
import LocationSort from "./LocationSort";

interface Props {
  onChange?: (sort: SortModel, query?: AreaSearchQuery) => void;
  onHide: () => void;
  sort: SortModel;
  sortQuery?: AreaSearchQuery;
}

const CustomizeMapSortModal: React.FC<Props> = ({ onChange, onHide, sort, sortQuery }) => {
  const analytics = useExtendedAnalytics("Sort", useAnalytics());
  useEffect(() => {
    analytics.initiated();
  }, [analytics]);

  const intl = useIntl();

  const [field, setField] = useState<SortField>(
    (sort?.[0]?.field?.name as SortField) ?? SortField.UPDATED_AT
  );
  const [order, setOrder] = useState<SortOrder>(sort?.[0]?.order ?? SortOrder.DESC);
  const [query, setQuery] = useState<AreaSearchQuery | undefined>(sortQuery);
  const [currentMode, setCurrentMode] = useState<GeoPointSortType>(
    (sortQuery?.mode as GeoPointSortType) ?? GeoPointSortType.CURRENT_USER_LOCATION
  );

  const applyButtonDisabled = useMemo(() => {
    return (
      field === SortField.GEOPOINT &&
      (!query ||
        ((isCoordinatesAreaSearchQuery(query) || isAddressAreaSearchQuery(query)) &&
          !query?.coordinates) ||
        (isEntityAreaSearchQuery(query) && !query.entity.geoPoint?.coordinates))
    );
  }, [field, query]);

  const sortOptions = useMemo(
    () =>
      [
        SortField.NAME,
        SortField.CREATED_AT,
        SortField.UPDATED_AT,
        SortField.LAST_ACTIVITY_DATE,
        SortField.NEXT_ACTIVITY,
        SortField.GEOPOINT,
      ].map((field) => ({
        label: getSortFieldDisplayName(intl, field),
        value: field,
      })),
    [intl]
  );

  const handleSelectField = useCallback(
    (name: SortField) => {
      setField(name);
      if (name === SortField.GEOPOINT) {
        setOrder(SortOrder.ASC);
      }
    },
    [setField, setOrder]
  );

  const handleSortingCancel = useCallback(() => {
    onHide?.();
    analytics.cancelled("");
  }, [analytics, onHide]);

  const handleApply = useCallback(() => {
    const entityTypes = [
      "universal",
      EntityType.COMPANY,
      EntityType.PERSON,
      EntityType.DEAL,
    ] as MapFilterEntityType[];
    const fieldDef = entityTypes.reduce<IField | undefined>((prev, value) => {
      return prev ?? getFieldModelByEntityType(value as MapFilterEntityType).getByName(field);
    }, undefined);

    const sort: SortModel = fieldDef
      ? [
          {
            field: fieldDef,
            order: field === SortField.GEOPOINT ? SortOrder.ASC : order,
          },
        ]
      : [];

    onChange?.(sort, field === SortField.GEOPOINT ? query : undefined);
    onHide();
    analytics.completed("", {
      sortBy: `${fieldDef?.displayName} ${order === SortOrder.ASC ? "asc" : "desc"}`,
    });
  }, [analytics, field, onChange, onHide, order, query]);

  return (
    <div className={styles.container}>
      <Row gutter={[layout.spacerS, layout.spacerM]}>
        <Col span={24}>
          <SelectField<SortField>
            className={styles.select}
            label={intl.formatMessage({
              id: "recordList.customization.sort.fieldSelect",
              defaultMessage: "Sort criteria",
              description: "Map Customization sort field select label",
            })}
            onChange={handleSelectField}
            options={sortOptions}
            value={field}
          />
        </Col>

        <Col span={24}>
          {field !== SortField.GEOPOINT ? (
            <SelectField<SortOrder>
              className={styles.select}
              label={intl.formatMessage({
                id: "recordList.customization.sort.orderSelect",
                defaultMessage: "Sort direction",
                description: "Map Customization sort direction select label",
              })}
              onChange={setOrder}
              value={order}
            >
              <Select.Option value={SortOrder.DESC}>
                {getSortDirectionNameForField(intl, field, SortOrder.DESC)}
              </Select.Option>
              <Select.Option value={SortOrder.ASC}>
                {getSortDirectionNameForField(intl, field, SortOrder.ASC)}
              </Select.Option>
            </SelectField>
          ) : (
            <LocationSort
              mode={currentMode}
              onModeChange={setCurrentMode}
              onQueryChange={setQuery}
              query={query}
            />
          )}
        </Col>

        <Col span={24}>
          <Row gutter={layout.spacerS} justify="end">
            <Col>
              <Button onClick={handleSortingCancel}>
                {intl.formatMessage({
                  id: "recordList.customization.sort.cancel",
                  defaultMessage: "Cancel",
                  description: "Map Customization sort select cancel",
                })}
              </Button>
            </Col>
            <Col>
              <Button disabled={applyButtonDisabled} onClick={handleApply} type="primary">
                {intl.formatMessage({
                  id: "recordList.customization.sort.apply",
                  defaultMessage: "Apply",
                  description: "Map Customization sort select apply",
                })}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default CustomizeMapSortModal;
